import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Loader2, List } from "lucide-react";
import { toast } from "sonner";

import { useMedTypes, isStripType, unitLabelOf } from "@/lib/medTypes";
import { MedTypeManager } from "@/components/MedTypeManager";

const empty = {
  name: "", generic_name: "", manufacturer: "", category: "General", subcategory: "", med_type: "tablet",
  batch_no: "", hsn_code: "3004", pack_size: 10, strip_mrp: "", purchase_price: "",
  gst_percent: 12, stock_strips: 0, stock_loose_tablets: 0, expiry_date: "", rack: "", barcode: "",
};

export default function AddMedicine() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [catTree, setCatTree] = useState([]);
  const [typesVer, setTypesVer] = useState(0);
  const medTypes = useMedTypes(typesVer);
  const [typeDialog, setTypeDialog] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCatTree(data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Medicine name is required");
    setSaving(true);
    try {
      await api.post("/medicines", {
        ...form,
        pack_size: isStripType(form.med_type) ? (Number(form.pack_size) || 10) : 1,
        strip_mrp: Number(form.strip_mrp) || 0,
        purchase_price: Number(form.purchase_price) || 0,
        gst_percent: Number(form.gst_percent) || 12,
        stock_strips: Number(form.stock_strips) || 0,
        stock_loose_tablets: Number(form.stock_loose_tablets) || 0,
      });
      toast.success(`${form.name} added to inventory`);
      setForm(empty);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const F = (label, key, type = "text", placeholder = "", testid) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 h-11"
      />
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5" data-testid="add-medicine-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Add Medicine</h1>
          <p className="text-sm text-slate-500">Add a new medicine to inventory manually.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/medicines")} data-testid="go-medicines-btn">
          <List className="w-4 h-4 mr-2" /> All Medicines
        </Button>
      </div>

      <Card className="p-6 border-slate-200">
        <form onSubmit={save}>
          <div className="flex items-center gap-2 mb-4">
            <PlusCircle className="w-4 h-4 text-emerald-700" />
            <h3 className="font-display font-bold">Medicine Details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {F("Medicine Name *", "name", "text", "e.g. Dolo 650", "am-name")}
            {F("Generic / Salt", "generic_name", "text", "e.g. Paracetamol 650mg", "am-generic")}
            {F("Manufacturer", "manufacturer", "text", "e.g. Micro Labs", "am-manufacturer")}
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category || "General"} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                <SelectTrigger data-testid="am-category" className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[...new Set(["General", ...catTree.map((c) => c.name)])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sub-Category</Label>
              <Select value={form.subcategory || "none"} onValueChange={(v) => setForm({ ...form, subcategory: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="am-subcategory" className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— None —</SelectItem>
                  {(catTree.find((c) => c.name === form.category)?.children || []).map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Medicine Type</Label>
              <Select value={form.med_type} onValueChange={(v) => setForm({ ...form, med_type: v, ...(isStripType(v) ? {} : { pack_size: 1, stock_loose_tablets: 0 }) })}>
                <SelectTrigger data-testid="am-med-type" className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {medTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  <div className="border-t border-slate-100 mt-1 p-1">
                    <button type="button" data-testid="am-type-manage" className="w-full text-left text-xs font-semibold text-emerald-700 rounded px-2 py-1.5 hover:bg-emerald-50" onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => setTypeDialog(true)}>+ Add / Manage Types</button>
                  </div>
                </SelectContent>
              </Select>
              <button type="button" onClick={() => setTypeDialog(true)} className="text-[11px] text-emerald-700 font-semibold mt-1 hover:underline" data-testid="am-manage-types-link">+ Add / Manage Types</button>
            </div>
            {F("Batch No.", "batch_no", "text", "e.g. DL2024A", "am-batch")}
            {F("HSN Code", "hsn_code", "text", "3004", "am-hsn")}
            {isStripType(form.med_type) && F("Pack Size (tablets per strip)", "pack_size", "number", "10", "am-pack")}
            {F(`${unitLabelOf(form)} MRP (₹)`, "strip_mrp", "number", "0.00", "am-mrp")}
            {F(`Purchase Cost (₹/${unitLabelOf(form).toLowerCase()})`, "purchase_price", "number", "0.00", "am-cost")}
            {F("GST %", "gst_percent", "number", "12", "am-gst")}
            {F(`Opening Stock (${unitLabelOf(form).toLowerCase()}s)`, "stock_strips", "number", "0", "am-strips")}
            {isStripType(form.med_type) && F("Opening Loose Tablets", "stock_loose_tablets", "number", "0", "am-loose")}
            {F("Expiry Date (YYYY-MM-DD)", "expiry_date", "text", "2027-06-30", "am-expiry")}
            {F("Rack / Shelf", "rack", "text", "R-1", "am-rack")}
            {F("Barcode", "barcode", "text", "8901234567890", "am-barcode")}
          </div>
          <div className="flex gap-3 mt-6">
            <Button type="submit" disabled={saving || !form.name} data-testid="add-medicine-submit" className="bg-emerald-900 hover:bg-emerald-800 h-11 px-8">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlusCircle className="w-4 h-4 mr-2" />}
              Save Medicine
            </Button>
            <Button type="button" variant="outline" onClick={() => setForm(empty)}>Clear</Button>
          </div>
        </form>
      </Card>

      <MedTypeManager open={typeDialog} onOpenChange={setTypeDialog} types={medTypes} onSaved={() => setTypesVer((v) => v + 1)} />
    </div>
  );
}
